import { normalizeYmd } from './employeeTimeEntryHours.js';

/** Raw hour buckets from time entries (payroll / Lohnbuchhaltung). */
export type PayrollHoursByType = {
  work: number;
  vacation: number;
  sickness: number;
  public_holiday: number;
  overtime_comp: number;
};

export type EmployeeCompensationType = 'hourly' | 'salary';

export type PayrollSummaryDerived = {
  /** Arbeit minus Stundenausgleich (nicht negativ). */
  net_work_hours: number;
  /** Abrechnungsstunden: net_work + Urlaub + Krank + Feiertag. */
  billable_hours: number;
  allowance_per_hour: number;
  allowance_pay: number;
  wage_pay: number;
  forecasted_gross_pay: number;
};

export type DerivePayrollSummaryOptions = {
  compensationType?: EmployeeCompensationType;
  monthlySalary?: number;
  /** Abrechnungszeitraum – für Fixum-Anteil (Kalendertage). */
  periodFrom?: string;
  periodTo?: string;
  employeeStartDate?: string | null;
  employeeEndDate?: string | null;
};

export function normalizeCompensationType(value: unknown): EmployeeCompensationType {
  return value === 'salary' ? 'salary' : 'hourly';
}

export function roundPayrollHours(n: number): number {
  return Math.round(n * 100) / 100;
}

export function roundPayrollMoney(n: number): number {
  return Math.round(n * 100) / 100;
}

function parseYmdUtc(ymd: string): Date | null {
  const normalized = normalizeYmd(ymd);
  if (!normalized) return null;
  const [y, m, d] = normalized.split('-').map((x) => Number.parseInt(x, 10));
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return null;
  return new Date(Date.UTC(y, m - 1, d));
}

function ymdFromUtcDate(d: Date): string {
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function daysInUtcMonth(year: number, monthIndex0: number): number {
  return new Date(Date.UTC(year, monthIndex0 + 1, 0)).getUTCDate();
}

/** True if employment window overlaps the inclusive [from, to] range. */
export function employmentOverlapsRange(
  from: string,
  to: string,
  employeeStartDate: string | null | undefined,
  employeeEndDate?: string | null | undefined,
): boolean {
  const rangeFrom = normalizeYmd(from);
  const rangeTo = normalizeYmd(to);
  if (!rangeFrom || !rangeTo || rangeFrom > rangeTo) return false;
  const start = normalizeYmd(employeeStartDate) ?? '0000-01-01';
  const end = normalizeYmd(employeeEndDate) ?? '9999-12-31';
  return start <= rangeTo && end >= rangeFrom;
}

/**
 * Fixum anteilig nach Kalendertagen:
 * je Kalendermonat im Zeitraum: monthly_salary × (Beschäftigungstage ∩ Monat ∩ Zeitraum) / Monatstage.
 * Deckt Mid-Month-Eintritt/-Austritt und Mehr-Monats-Zeiträume.
 */
export function deriveProratedMonthlySalary(params: {
  monthlySalary: number;
  from: string;
  to: string;
  employeeStartDate?: string | null;
  employeeEndDate?: string | null;
}): number {
  const monthlySalary = Math.max(0, Number(params.monthlySalary) || 0);
  if (monthlySalary <= 0) return 0;

  const rangeFrom = normalizeYmd(params.from);
  const rangeTo = normalizeYmd(params.to);
  if (!rangeFrom || !rangeTo || rangeFrom > rangeTo) return 0;

  if (!employmentOverlapsRange(rangeFrom, rangeTo, params.employeeStartDate, params.employeeEndDate)) {
    return 0;
  }

  const empStart = normalizeYmd(params.employeeStartDate) ?? '0000-01-01';
  const empEnd = normalizeYmd(params.employeeEndDate) ?? '9999-12-31';
  const clipFrom = rangeFrom > empStart ? rangeFrom : empStart;
  const clipTo = rangeTo < empEnd ? rangeTo : empEnd;
  if (clipFrom > clipTo) return 0;

  const startDate = parseYmdUtc(clipFrom);
  const endDate = parseYmdUtc(clipTo);
  if (!startDate || !endDate) return 0;

  let total = 0;
  let cursor = new Date(Date.UTC(startDate.getUTCFullYear(), startDate.getUTCMonth(), 1));
  const lastMonth = new Date(Date.UTC(endDate.getUTCFullYear(), endDate.getUTCMonth(), 1));

  while (cursor.getTime() <= lastMonth.getTime()) {
    const year = cursor.getUTCFullYear();
    const month = cursor.getUTCMonth();
    const monthDays = daysInUtcMonth(year, month);
    const monthStart = ymdFromUtcDate(new Date(Date.UTC(year, month, 1)));
    const monthEnd = ymdFromUtcDate(new Date(Date.UTC(year, month, monthDays)));
    const segFrom = clipFrom > monthStart ? clipFrom : monthStart;
    const segTo = clipTo < monthEnd ? clipTo : monthEnd;
    if (segFrom <= segTo) {
      const a = parseYmdUtc(segFrom);
      const b = parseYmdUtc(segTo);
      if (a && b) {
        const employedDays = Math.round((b.getTime() - a.getTime()) / 86_400_000) + 1;
        total += monthlySalary * (employedDays / monthDays);
      }
    }
    cursor = new Date(Date.UTC(year, month + 1, 1));
  }

  return roundPayrollMoney(total);
}

/**
 * Lohnlogik:
 * - Urlaub, Krank, Feiertag und Netto-Arbeit werden mit Stundenlohn vergütet.
 * - Stundenausgleich reduziert die Arbeitsstunden (nicht additiv).
 * - Die Stundenbasis für Lohnart 1000 wird separat im Lohnbuchungs-Export berechnet.
 * - Fixum (`salary`): anteiliges monthly_salary über Zeitraum/Beschäftigung, keine Stunden-Zulage.
 */
export function derivePayrollSummary(
  hours: PayrollHoursByType,
  hourlyWage: number,
  allowancePerHour: number,
  options?: DerivePayrollSummaryOptions,
): PayrollSummaryDerived {
  const netWork = roundPayrollHours(Math.max(0, hours.work - hours.overtime_comp));
  const billable = roundPayrollHours(
    netWork + hours.vacation + hours.sickness + hours.public_holiday,
  );
  const compensationType = normalizeCompensationType(options?.compensationType);
  const monthlySalary = Math.max(0, Number(options?.monthlySalary) || 0);

  if (compensationType === 'salary') {
    const wagePay =
      options?.periodFrom && options?.periodTo
        ? deriveProratedMonthlySalary({
            monthlySalary,
            from: options.periodFrom,
            to: options.periodTo,
            employeeStartDate: options.employeeStartDate,
            employeeEndDate: options.employeeEndDate,
          })
        : roundPayrollMoney(monthlySalary);
    return {
      net_work_hours: netWork,
      billable_hours: billable,
      allowance_per_hour: 0,
      allowance_pay: 0,
      wage_pay: wagePay,
      forecasted_gross_pay: wagePay,
    };
  }

  const wagePay = roundPayrollMoney(billable * hourlyWage);
  const allowancePay = roundPayrollMoney(billable * allowancePerHour);
  const forecasted = roundPayrollMoney(wagePay + allowancePay);

  return {
    net_work_hours: netWork,
    billable_hours: billable,
    allowance_per_hour: allowancePerHour,
    allowance_pay: allowancePay,
    wage_pay: wagePay,
    forecasted_gross_pay: forecasted,
  };
}
